import { Check, ChevronRight, Lock, Music2, Plus, UploadCloud } from "lucide-react";

const MEDITATION_CATEGORIES = [
  'çakra', 'karmik', 'şifa', 'bağ koparma', 'bolluk',
  'özgüven', 'ilişki', 'uyku', 'sabah enerjisi', 'stres'
];

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { adminFetch } from "@/hooks/use-admin-session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type MeditationDraft = {
  id?: string;
  title: string;
  description: string;
  durationMinutes: number;
  programDays: string;
  xpPoints: string;
  level: "beginner" | "intermediate" | "advanced";
  coverImageUrl: string;
  audioUrl: string;
  active: boolean;
  isPremium: boolean;
  tagsText: string;
  price?: number;
  store_product_id?: string | null;
  music_url?: string;
  music_volume?: number;
  kategori_id?: string;
  content_language?: 'tr' | 'en' | 'es';
};

// Google Play / RevenueCat'te tanımlı 3 sabit tek seferlik içerik fiyat kademesi.
export const PRICE_TIERS = [
  { price: 999, storeProductId: 'icerik_999', label: '₺999,00 (icerik_999)' },
  { price: 1299, storeProductId: 'icerik_1299', label: '₺1.299,00 (icerik_1299)' },
  { price: 1499, storeProductId: 'icerik_1499', label: '₺1.499,00 (icerik_1499)' },
] as const;

type ContentMode = "meditation" | "breath";

type MeditationMediaItem = {
  path: string;
  name: string;
  url: string;
};

function buildMediaLibraryItem(url: string, fallbackName: string): MeditationMediaItem | null {
  const trimmed = String(url ?? "").trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    const rawName = parsed.pathname.split("/").filter(Boolean).pop() ?? fallbackName;
    return {
      path: parsed.pathname || trimmed,
      name: decodeURIComponent(rawName) || fallbackName,
      url: trimmed,
    };
  } catch {
    const rawName = trimmed.split("/").filter(Boolean).pop() ?? fallbackName;
    return {
      path: trimmed,
      name: rawName || fallbackName,
      url: trimmed,
    };
  }
}

const LEVEL_OPTIONS: Array<{ value: MeditationDraft["level"]; label: string }> = [
  { value: "beginner", label: "Başlangıç" },
  { value: "intermediate", label: "Orta" },
  { value: "advanced", label: "İleri" },
];

function toDurationInput(minutes: number) {
  const totalSeconds = Math.max(0, Math.round(Math.max(0, Number(minutes) || 0) * 60));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return secs ? `${mins}:${String(secs).padStart(2, "0")}` : String(mins);
}

function parseDurationInput(rawValue: string) {
  const value = rawValue.trim();
  if (!value) return null;

  if (value.includes(":")) {
    const match = value.match(/^(\d+)\s*:\s*(\d{1,2})$/);
    if (!match) return null;

    const minutesPart = Number(match[1]);
    const secondsPart = Number(match[2]);
    if (!Number.isFinite(minutesPart) || !Number.isFinite(secondsPart) || secondsPart < 0 || secondsPart > 59) {
      return null;
    }

    return Math.max(0, (minutesPart * 60 + secondsPart) / 60);
  }

  const minutes = Number(value.replace(",", "."));
  if (!Number.isFinite(minutes) || minutes < 0) return null;
  return minutes;
}

export function NumerolojiMeditationEditor({
  draft,
  setDraft,
  onClose,
  onSave,
  onCreateVersion,
  onNavigateSibling,
  siblings,
  coverPrefilledFromSibling,
  isSaving,
  saveDisabled = false,
  saveLabel,
  mode = "meditation",
}: {
  draft: MeditationDraft;
  setDraft: (next: MeditationDraft) => void;
  onClose: () => void;
  onSave: (payload: any) => void;
  onCreateVersion?: (lang: 'en' | 'es') => void;
  onNavigateSibling?: (id: string) => void;
  siblings?: Array<{ id: string; content_language: string }>;
  coverPrefilledFromSibling?: boolean;
  isSaving: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  mode?: ContentMode;
}) {
  const isEdit = Boolean(draft.id);
  const isBreathMode = mode === "breath";
  const contentLabelPlural = isBreathMode ? "Nefes Seansları" : "Meditasyonlar";
  const title = isBreathMode
    ? isEdit
      ? "Nefes Seansını Düzenle"
      : "Yeni Nefes Seansı Ekle"
    : isEdit
      ? "Meditasyonu Düzenle"
      : "Yeni Meditasyon Ekle";

  const tur = isBreathMode ? 'nefes' : 'meditasyon';
  const { data: kategorilerData } = useQuery({
    queryKey: ['kategoriler', tur],
    queryFn: () => adminFetch(`/api/admin/kategoriler?tur=${tur}`).then(r => r.json()),
  });
  const kategoriler: any[] = kategorilerData?.items ?? [];

  const coverFileRef = useRef<HTMLInputElement | null>(null);
  const audioFileRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [durationInput, setDurationInput] = useState(() => toDurationInput(draft.durationMinutes));
  const [durationError, setDurationError] = useState<string | null>(null);
  const [programDaysError, setProgramDaysError] = useState<string | null>(null);
  const [mediaLibraryLoading, setMediaLibraryLoading] = useState(false);
  const [audioLibrary, setAudioLibrary] = useState<MeditationMediaItem[]>([]);
  const [coverLibrary, setCoverLibrary] = useState<MeditationMediaItem[]>([]);

  const normalizedId = useMemo(() => String(draft.id ?? "").trim(), [draft.id]);
  const normalizedTitle = useMemo(() => draft.title.trim(), [draft.title]);
  const visibleAudioLibrary = useMemo(() => {
    const current = buildMediaLibraryItem(draft.audioUrl, "secili-ses");
    return Array.from(new Map([...(current ? [[current.url, current] as const] : []), ...audioLibrary.map((item) => [item.url, item] as const)]).values());
  }, [audioLibrary, draft.audioUrl]);
  const visibleCoverLibrary = useMemo(() => {
    const current = buildMediaLibraryItem(draft.coverImageUrl, "secili-kapak");
    return Array.from(new Map([...(current ? [[current.url, current] as const] : []), ...coverLibrary.map((item) => [item.url, item] as const)]).values());
  }, [coverLibrary, draft.coverImageUrl]);

  const mediaApiBase = isBreathMode ? "/api/admin/numeroloji/breaths/media" : "/api/admin/numeroloji/meditations/media";

  const loadMediaLibrary = async () => {
    setMediaLibraryLoading(true);
    setMediaError(null);

    try {
      const buildUrl = (mediaType: "audio" | "image") => {
        const sp = new URLSearchParams();
        sp.set("mediaType", mediaType);
        if (normalizedId) sp.set(isBreathMode ? "breathId" : "meditationId", normalizedId);
        if (normalizedTitle) sp.set(isBreathMode ? "breathTitle" : "meditationTitle", normalizedTitle);
        return `${mediaApiBase}?${sp.toString()}`;
      };

      const [audioRes, imageRes] = await Promise.all([adminFetch(buildUrl("audio")), adminFetch(buildUrl("image"))]);
      const [audioJson, imageJson] = await Promise.all([audioRes.json().catch(() => null), imageRes.json().catch(() => null)]);

      if (!audioRes.ok) {
        throw new Error(typeof (audioJson as any)?.error === "string" ? (audioJson as any).error : "Ses dosyaları alınamadı.");
      }
      if (!imageRes.ok) {
        throw new Error(typeof (imageJson as any)?.error === "string" ? (imageJson as any).error : "Kapak görselleri alınamadı.");
      }

      const toItems = (rows: any): MeditationMediaItem[] =>
        (Array.isArray(rows) ? rows : [])
          .map((it: any) => ({
            path: String(it?.path ?? ""),
            name: String(it?.name ?? ""),
            url: String(it?.url ?? ""),
          }))
          .filter((it: MeditationMediaItem) => it.path && it.url);

      setAudioLibrary(toItems((audioJson as any)?.items));
      setCoverLibrary(toItems((imageJson as any)?.items));
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : "Yüklenen dosyalar alınamadı.");
    } finally {
      setMediaLibraryLoading(false);
    }
  };

  useEffect(() => {
    void loadMediaLibrary();
  }, [mediaApiBase, normalizedId, normalizedTitle]);

  useEffect(() => {
    setDurationInput(toDurationInput(draft.durationMinutes));
  }, [draft.durationMinutes]);

  const savePayload = () => {
    const parsedDuration = parseDurationInput(durationInput);
    if (parsedDuration === null) {
      setDurationError("Süreyi dakika veya mm:ss formatında girin. Örn: 10 veya 2:34");
      return;
    }

    setDurationError(null);
    const programDaysValue = String(draft.programDays ?? "").trim();
    const parsedProgramDays = Number(programDaysValue);

    if (programDaysValue.length > 0 && (!Number.isFinite(parsedProgramDays) || parsedProgramDays <= 0)) {
      setProgramDaysError("Program süresini pozitif tam sayı olarak girin. Örn: 21");
      return;
    }

    setProgramDaysError(null);

    onSave({
      ...draft,
      durationMinutes: parsedDuration,
      programDays: Number.isFinite(parsedProgramDays) && parsedProgramDays > 0 ? Math.round(parsedProgramDays) : undefined,
      tags: draft.tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      coverImageUrl: draft.coverImageUrl.trim() || undefined,
      audioUrl: draft.audioUrl.trim() || undefined,
      price: draft.price ?? 0,
      store_product_id: draft.store_product_id || null,
      music_url: draft.music_url?.trim() || null,
      music_volume: draft.music_volume ?? null,
      kategori_id: draft.kategori_id || null,
    });
  };

  const uploadMedia = async (file: File, mediaType: "image" | "audio") => {
    setMediaError(null);

    if (!file) return;
    if (mediaType === "image" && file.size > 6 * 1024 * 1024) {
      setMediaError("Kapak görseli için max 6MB dosya yükleyebilirsiniz.");
      return;
    }
    if (mediaType === "audio" && file.size > 20 * 1024 * 1024) {
      setMediaError("Ses dosyası için max 20MB dosya yükleyebilirsiniz.");
      return;
    }

    mediaType === "image" ? setIsUploadingCover(true) : setIsUploadingAudio(true);

    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });

      const res = await adminFetch(mediaApiBase, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mediaType,
          contentType: file.type || (mediaType === "image" ? "image/jpeg" : "audio/mpeg"),
          dataBase64,
          meditationId: isBreathMode ? undefined : draft.id,
          meditationTitle: isBreathMode ? undefined : draft.title,
          breathId: isBreathMode ? draft.id : undefined,
          breathTitle: isBreathMode ? draft.title : undefined,
        }),
      });

      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Dosya yüklenemedi.");
      }

      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Dosya URL alınamadı.");

      if (mediaType === "image") {
        setDraft({ ...draft, coverImageUrl: url });
      } else {
        setDraft({ ...draft, audioUrl: url });
      }

      await loadMediaLibrary();
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : "Dosya yüklenemedi.");
    } finally {
      mediaType === "image" ? setIsUploadingCover(false) : setIsUploadingAudio(false);
    }
  };

  const TR_VOICE_ID = "6SGlfUEeyAQoAhSmnJuV";
  const EN_VOICE_ID = "ZqvIIuD5aI9JFejebHiH";
  const ES_VOICE_ID = "Cmqnney5svFebDMl5Y9L";

  function defaultVoiceForLang(lang: 'tr' | 'en' | 'es' | undefined): string {
    if (lang === 'en') return EN_VOICE_ID;
    if (lang === 'es') return ES_VOICE_ID;
    return TR_VOICE_ID;
  }

  const [activeTab, setActiveTab] = useState<"manuel" | "ai">("manuel");
  const [aiTitle, setAiTitle] = useState("");
  const [aiText, setAiText] = useState("");
  const [aiVoiceId, setAiVoiceId] = useState(() => defaultVoiceForLang(draft.content_language));
  const [aiMusicId, setAiMusicId] = useState("");
  const [aiMusicVolume, setAiMusicVolume] = useState(30);
  const [aiSpeed, setAiSpeed] = useState(0.8);
  const [musicList, setMusicList] = useState<{ id: string; title: string; audio_url: string }[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const isGeneratingRef = useRef(false);
  const [aiElapsed, setAiElapsed] = useState(0);
  const aiTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const aiPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<{ audioUrls: string[]; fileName: string; durationMinutes?: number } | null>(null);
  const [aiResumed, setAiResumed] = useState(false);

  useEffect(() => {
    return () => {
      if (aiPollRef.current) clearInterval(aiPollRef.current);
    };
  }, []);
  const [aiPlaying, setAiPlaying] = useState(false);
  const meditationPreviewRef = useRef<HTMLAudioElement | null>(null);
  const musicPreviewRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    adminFetch("/api/admin/meditation-music")
      .then(r => r.json())
      .then(d => { if (Array.isArray(d?.items)) setMusicList(d.items); })
      .catch(() => {});
  }, []);

  const baseInput =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50";

  const formDisabled = Boolean(isSaving || isUploadingCover || isUploadingAudio);
  const isSaveButtonDisabled = Boolean(formDisabled || saveDisabled);

  const tabBase = "px-5 py-2 text-sm font-semibold rounded-full transition-colors";
  const tabActive = "bg-blue-600 text-white shadow-sm";
  const tabInactive = "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100";

  return (
    <div className="rounded-3xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/40 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
          <span>İçerik Yönetimi</span>
          <ChevronRight className="h-4 w-4" />
          <span>{contentLabelPlural}</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-slate-900 dark:text-slate-100">{title}</span>
        </div>

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {isBreathMode ? "Nefes seansı bilgilerini düzenleyin ve kaydedin." : "Meditasyon bilgilerini düzenleyin ve kaydedin."}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full bg-white/80 dark:bg-slate-950/40"
              onClick={onClose}
              disabled={formDisabled}
            >
              Vazgeç
            </Button>
            {activeTab === "manuel" && (
              <Button className="rounded-full" onClick={savePayload} disabled={isSaveButtonDisabled}>
                {isSaving ? "Kaydediliyor..." : saveLabel ?? "Kaydet"}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-2 rounded-full border border-slate-200 bg-slate-100/60 p-1 w-fit dark:border-slate-700 dark:bg-slate-800/60">
          <button type="button" className={cn(tabBase, activeTab === "manuel" ? tabActive : tabInactive)} onClick={() => setActiveTab("manuel")}>
            Manuel
          </button>
          <button type="button" className={cn(tabBase, activeTab === "ai" ? tabActive : tabInactive)} onClick={() => setActiveTab("ai")}>
            AI ile Oluştur
          </button>
        </div>
      </div>

      {activeTab === "ai" && (
        <div className="rounded-3xl border border-slate-200 bg-white/70 p-6 dark:border-slate-800 dark:bg-slate-950/40 space-y-5 max-w-2xl">
          <div className="text-base font-bold text-slate-900 dark:text-white">AI ile Meditasyon Oluştur</div>

          <div className="grid gap-2">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Başlık</Label>
            <input
              className={baseInput}
              value={aiTitle}
              onChange={e => setAiTitle(e.target.value)}
              placeholder="Kaydedilecek meditasyon başlığı"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Meditasyon Metni</Label>
            <textarea
              className={cn(baseInput, "min-h-[260px] resize-y")}
              value={aiText}
              onChange={e => setAiText(e.target.value)}
              placeholder="Seslendirilecek meditasyon metnini buraya girin..."
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Arka Plan Müziği</Label>
            <select
              className={baseInput}
              value={aiMusicId}
              onChange={e => setAiMusicId(e.target.value)}
            >
              <option value="">— Müzik seçme —</option>
              {musicList.map(m => (
                <option key={m.id} value={m.id}>{m.title}</option>
              ))}
            </select>
          </div>

          <div className="grid gap-2">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Müzik Ses Seviyesi: {aiMusicVolume}%
            </Label>
            <input
              type="range"
              min={0}
              max={100}
              value={aiMusicVolume}
              onChange={e => {
                const v = Number(e.target.value);
                setAiMusicVolume(v);
                if (musicPreviewRef.current) musicPreviewRef.current.volume = v / 100;
              }}
              className="w-full accent-blue-600"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Konuşma Hızı: {aiSpeed.toFixed(2)}x
            </Label>
            <input
              type="range"
              min={0.7}
              max={1.2}
              step={0.05}
              value={aiSpeed}
              onChange={e => setAiSpeed(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Voice ID (varsayılan native ses otomatik seçildi)
            </Label>
            <input
              className={baseInput}
              value={aiVoiceId}
              onChange={e => setAiVoiceId(e.target.value)}
              placeholder={defaultVoiceForLang(draft.content_language)}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Varsayılan native ses otomatik seçildi. Farklı bir ses kullanmak isterseniz ElevenLabs → Voices'tan ID kopyalayıp buraya yapıştırabilirsiniz.
            </p>
          </div>

          {aiError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {aiError}
            </div>
          )}

          {aiResult && (() => {
            const selectedMusicUrl = musicList.find(m => m.id === aiMusicId)?.audio_url;
            return (
              <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 space-y-4">
                <div className="text-sm font-semibold text-green-800 dark:text-green-300">Ses oluşturuldu ✓</div>

                {/* Hidden audio elements controlled via refs */}
                <audio
                  ref={meditationPreviewRef}
                  src={aiResult.audioUrls[0]}
                  onEnded={() => {
                    if (musicPreviewRef.current) { musicPreviewRef.current.pause(); musicPreviewRef.current.currentTime = 0; }
                    setAiPlaying(false);
                  }}
                />
                {selectedMusicUrl && (
                  <audio ref={musicPreviewRef} src={selectedMusicUrl} loop />
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={aiPlaying}
                    onClick={() => {
                      const med = meditationPreviewRef.current;
                      const mus = musicPreviewRef.current;
                      if (!med) return;
                      med.currentTime = 0;
                      if (mus) { mus.volume = aiMusicVolume / 100; mus.currentTime = 0; mus.play().catch(() => {}); }
                      med.play().catch(() => {});
                      setAiPlaying(true);
                    }}
                  >
                    ▶ Önizlemeyi Başlat
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={!aiPlaying}
                    onClick={() => {
                      const med = meditationPreviewRef.current;
                      const mus = musicPreviewRef.current;
                      if (med) { med.pause(); med.currentTime = 0; }
                      if (mus) { mus.pause(); mus.currentTime = 0; }
                      setAiPlaying(false);
                    }}
                  >
                    ■ Durdur
                  </Button>
                </div>

                {aiResult.audioUrls.map((url, i) => (
                  <div key={i}>
                    {aiResult.audioUrls.length > 1 && (
                      <p className="text-xs text-slate-500 mt-2">{i + 1}. Bölüm</p>
                    )}
                    <audio controls src={url} className="w-full mt-1" />
                  </div>
                ))}

                <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-800 dark:border-amber-400/30 dark:text-amber-300">
                  Ses üretildi ve önizleme için hazır. Kalıcı olarak kaydetmek için aşağıdaki butona basarak formu doldurun, ardından <strong>Manuel</strong> sekmesindeki <strong>Kaydet</strong> butonuna tıklayın.
                </div>

                <Button
                  type="button"
                  className="rounded-full"
                  onClick={() => {
                    const med = meditationPreviewRef.current;
                    const mus = musicPreviewRef.current;
                    if (med) { med.pause(); med.currentTime = 0; }
                    if (mus) { mus.pause(); mus.currentTime = 0; }
                    setAiPlaying(false);
                    setDraft({ ...draft, title: aiTitle, audioUrl: aiResult!.audioUrls.join(','), music_url: musicList.find(m => m.id === aiMusicId)?.audio_url, music_volume: aiMusicVolume, ...(aiResult!.durationMinutes ? { durationMinutes: aiResult!.durationMinutes } : {}) });
                    setActiveTab("manuel");
                  }}
                >
                  Manuel Forma Aktar →
                </Button>
              </div>
            );
          })()}

          <Button
            type="button"
            className="rounded-full"
            onClick={async () => {
              if (isGeneratingRef.current) return;
              isGeneratingRef.current = true;
              setAiError(null);
              setAiResult(null);
              setAiResumed(false);
              setAiElapsed(0);
              setAiLoading(true);
              aiTimerRef.current = setInterval(() => setAiElapsed(s => s + 1), 1000);

              const stopLoading = () => {
                if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current = null; }
                if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
                isGeneratingRef.current = false;
                setAiLoading(false);
              };

              let jobId: string;
              try {
                const selectedMusic = musicList.find(m => m.id === aiMusicId);
                const res = await adminFetch("/api/admin/elevenlabs/generate", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    text: aiText,
                    title: aiTitle,
                    voiceId: aiVoiceId.trim() || defaultVoiceForLang(draft.content_language),
                    musicUrl: selectedMusic?.audio_url,
                    musicVolume: aiMusicVolume,
                    speed: aiSpeed,
                  }),
                });
                const json = await res.json();
                if (!res.ok) throw new Error(json.error ?? "Oluşturulamadı.");
                jobId = json.jobId;
                if (json.resumed) setAiResumed(true);
              } catch (err) {
                setAiError(err instanceof Error ? err.message : "Bir hata oluştu.");
                stopLoading();
                return;
              }

              // Poll for job result every 2.5 seconds
              aiPollRef.current = setInterval(async () => {
                try {
                  const res = await adminFetch(`/api/admin/elevenlabs/generate/status/${jobId}`);
                  const json = await res.json();
                  if (!res.ok || !json.ok) {
                    stopLoading();
                    setAiError(json?.error ?? "Durum sorgulanamadı.");
                    return;
                  }
                  if (json.status === 'done') {
                    stopLoading();
                    setAiResult({
                      audioUrls: Array.isArray(json.audioUrls) ? json.audioUrls : [],
                      fileName: json.fileName ?? '',
                      durationMinutes: json.durationMinutes,
                    });
                  } else if (json.status === 'error') {
                    stopLoading();
                    setAiError(json.error ?? 'Ses oluşturulamadı.');
                  }
                  // status === 'pending' → keep polling
                } catch {
                  // Network blip — keep polling, don't stop
                }
              }, 2500);
            }}
            disabled={!aiTitle.trim() || !aiText.trim() || aiLoading}
          >
            {aiLoading ? "Oluşturuluyor..." : "Oluştur"}
          </Button>
          {aiLoading && (() => {
            const estimatedSeconds = Math.max(15, Math.ceil(aiText.length / 1000) * 15);
            const pct = Math.min(100, Math.round((aiElapsed / estimatedSeconds) * 100));
            const remaining = Math.max(0, estimatedSeconds - aiElapsed);
            return (
              <div className="mt-3 flex flex-col gap-2">
                {aiResumed && (
                  <div className="text-xs text-blue-600 dark:text-blue-400">
                    Devam eden bir üretim bulundu — sonuç bekleniyor...
                  </div>
                )}
                <div className="flex justify-between text-xs text-slate-500">
                  <span className="animate-pulse">Ses oluşturuluyor...</span>
                  <span>{aiElapsed}s / ~{estimatedSeconds}s ({remaining > 0 ? `~${remaining}s kaldı` : 'bitiyor...'})</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-purple-500 transition-all duration-1000"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === "manuel" && (

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Genel Bilgiler</div>

            <div className="mt-5 grid gap-5">
              <div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">İçerik Dili</Label>
                  {isEdit && <Lock className="h-3.5 w-3.5 text-slate-400" />}
                </div>
                <div className={cn("mt-2 flex gap-2", isEdit && "opacity-50")}>
                  {([
                    { value: 'tr', label: '🇹🇷 Türkçe' },
                    { value: 'en', label: '🇬🇧 English' },
                    { value: 'es', label: '🇪🇸 Español' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={isEdit}
                      onClick={() => {
                        const newLang = opt.value;
                        setDraft({ ...draft, content_language: newLang });
                        setAiVoiceId(defaultVoiceForLang(newLang));
                      }}
                      className={cn(
                        "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                        isEdit && "cursor-not-allowed",
                        (draft.content_language ?? 'tr') === opt.value
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {!isEdit && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Bu içerik hangi dilde hazırlanıyorsa onu seç. Başlık, açıklama ve ses kaydı bu dilde olmalı.
                  </p>
                )}
                {isEdit && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">Versiyonlar:</span>
                    {(['tr', 'en', 'es'] as const).map((lang) => {
                      const flag = lang === 'tr' ? '🇹🇷' : lang === 'en' ? '🇬🇧' : '🇪🇸';
                      const sibling = siblings?.find((s) => s.content_language === lang);
                      const isCurrent = (draft.content_language ?? 'tr') === lang;
                      if (isCurrent) {
                        return (
                          <span
                            key={lang}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
                          >
                            <Check className="h-3 w-3" />
                            {flag} {lang.toUpperCase()}
                          </span>
                        );
                      }
                      if (sibling) {
                        return (
                          <button
                            key={lang}
                            type="button"
                            onClick={() => onNavigateSibling?.(sibling.id)}
                            className="inline-flex items-center gap-1 rounded-full border-2 border-green-500 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 dark:border-green-500/60 dark:text-green-400 dark:hover:bg-green-500/10"
                          >
                            {flag} {lang.toUpperCase()}
                          </button>
                        );
                      }
                      return (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => onCreateVersion?.(lang)}
                          className="inline-flex items-center gap-1 rounded-full border-2 border-dashed border-slate-300 px-3 py-1 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:border-slate-500"
                        >
                          <Plus className="h-3 w-3" />
                          {flag} {lang.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Başlık</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Örn: İçsel Huzur ve Zihinsel Berraklık"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Açıklama</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[180px] resize-none")}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder={isBreathMode ? "Nefes seansının kısa açıklaması..." : "Meditasyonun kısa açıklaması..."}
                />
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Seans Bilgisi</div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Süre (dk veya mm:ss)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="decimal"
                  value={durationInput}
                  onChange={(e) => {
                    setDurationInput(e.target.value);
                    if (durationError) setDurationError(null);
                  }}
                  onBlur={() => {
                    const parsedDuration = parseDurationInput(durationInput);
                    if (parsedDuration === null) {
                      setDurationError("Süreyi dakika veya mm:ss formatında girin. Örn: 10 veya 2:34");
                      return;
                    }

                    setDurationError(null);
                    setDraft({ ...draft, durationMinutes: parsedDuration });
                    setDurationInput(toDurationInput(parsedDuration));
                  }}
                  placeholder="Örn: 10 veya 2:34"
                />
                {durationError ? <div className="text-xs text-destructive">{durationError}</div> : null}
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Seviye</Label>
                <select
                  className={cn(baseInput, "h-12")}
                  value={draft.level}
                  onChange={(e) => setDraft({ ...draft, level: e.target.value as any })}
                >
                  {LEVEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Program Süresi (Gün)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={draft.programDays}
                  onChange={(e) => {
                    const normalized = e.target.value.replace(/[^0-9]/g, "");
                    if (programDaysError) setProgramDaysError(null);
                    setDraft({ ...draft, programDays: normalized });
                  }}
                  placeholder="Örn: 21 veya 40"
                />
                {programDaysError ? <div className="text-xs text-destructive">{programDaysError}</div> : null}
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tamamlama Puanı (XP)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={draft.xpPoints}
                  onChange={(e) => setDraft({ ...draft, xpPoints: e.target.value.replace(/[^0-9]/g, "") })}
                  placeholder="Örn: 10"
                />
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium">Ürün Kademesi</label>
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={draft.store_product_id ?? (draft.price ? "custom" : "free")}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "free") {
                      setDraft({ ...draft, price: 0, store_product_id: null });
                    } else if (v === "custom") {
                      setDraft({ ...draft, store_product_id: null });
                    } else {
                      const tier = PRICE_TIERS.find((t) => t.storeProductId === v);
                      if (tier) setDraft({ ...draft, price: tier.price, store_product_id: tier.storeProductId });
                    }
                  }}
                >
                  <option value="free">Ücretsiz</option>
                  {PRICE_TIERS.map((t) => (
                    <option key={t.storeProductId} value={t.storeProductId}>{t.label}</option>
                  ))}
                  <option value="custom">Serbest / Elle gir</option>
                </select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium">Fiyat (₺) — 0 ise ücretsiz</label>
                <input
                  type="number"
                  min={0}
                  value={draft.price ?? 0}
                  disabled={Boolean(draft.store_product_id)}
                  onChange={e => setDraft({ ...draft, price: Number(e.target.value) })}
                  className="border rounded px-3 py-2 text-sm disabled:opacity-60 disabled:bg-muted/40"
                  placeholder="0"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kategori</Label>
                <select
                  key={kategoriler.length}
                  className={cn(baseInput)}
                  value={draft.kategori_id ?? ''}
                  onChange={(e) => setDraft({ ...draft, kategori_id: e.target.value || undefined })}
                >
                  <option value="">— Kategori seç —</option>
                  {kategoriler.map((k: any) => (
                    <option key={k.id} value={k.id}>{k.isim}</option>
                  ))}
                </select>
              </div>

              <div className="grid gap-2 sm:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Etiketler (virgülle)</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.tagsText}
                  onChange={(e) => setDraft({ ...draft, tagsText: e.target.value })}
                  placeholder="genel, Karmik Borç 1"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {MEDITATION_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        const tag = `kategori:${cat}`;
                        const existing = draft.tagsText ? draft.tagsText.split(',').map(t => t.trim()) : [];
                        if (!existing.includes(tag)) {
                          setDraft({ ...draft, tagsText: [...existing, tag].join(', ') });
                        }
                      }}
                      className="px-2 py-1 text-xs rounded-full border border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="text-base font-bold text-slate-900 dark:text-white">Medya</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">(URL ile)</div>
            </div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kapak görsel URL</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.coverImageUrl}
                  onChange={(e) => setDraft({ ...draft, coverImageUrl: e.target.value })}
                  placeholder="https://..."
                />
                {coverPrefilledFromSibling && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Kaynak dildeki görsel otomatik kopyalandı, isterseniz değiştirebilirsiniz.
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ses URL (opsiyonel)</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.audioUrl}
                  onChange={(e) => setDraft({ ...draft, audioUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Arka Plan Müziği</Label>
                <select
                  key={musicList.length}
                  className={cn(baseInput)}
                  value={draft.music_url ?? ""}
                  onChange={(e) => setDraft({ ...draft, music_url: e.target.value || undefined })}
                >
                  <option value="">— Müzik seçme —</option>
                  {musicList.map(m => (
                    <option key={m.id} value={m.audio_url}>{m.title}</option>
                  ))}
                </select>
              </div>

              {draft.music_url && (
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Müzik Ses Seviyesi: {draft.music_volume ?? 30}%
                  </Label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={draft.music_volume ?? 30}
                    onChange={(e) => setDraft({ ...draft, music_volume: Number(e.target.value) })}
                    className="w-full accent-blue-600"
                  />
                </div>
              )}

              <input
                ref={coverFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void uploadMedia(file, "image");
                  e.currentTarget.value = "";
                }}
              />

              <input
                ref={audioFileRef}
                type="file"
                accept="audio/*,.mp3,.m4a,.wav,.ogg"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void uploadMedia(file, "audio");
                  e.currentTarget.value = "";
                }}
              />

              <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-6 text-slate-600 dark:border-slate-800 dark:bg-slate-950/20 dark:text-slate-300">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                    onClick={() => coverFileRef.current?.click()}
                    disabled={formDisabled}
                  >
                    <UploadCloud className="h-4 w-4" />
                    {isUploadingCover ? "Kapak yükleniyor..." : "Kapak görseli yükle"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                    onClick={() => audioFileRef.current?.click()}
                    disabled={formDisabled}
                  >
                    <UploadCloud className="h-4 w-4" />
                    {isUploadingAudio ? "Ses yükleniyor..." : "Ses dosyası yükle"}
                  </Button>
                </div>

                <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Dosyalar FTP'ye yüklenir ve URL alanları otomatik doldurulur.
                </div>

                {mediaError ? <div className="mt-2 text-xs text-destructive">{mediaError}</div> : null}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-950/20">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Önceden yüklenen dosyalar</div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => void loadMediaLibrary()}
                    disabled={mediaLibraryLoading || formDisabled}
                  >
                    {mediaLibraryLoading ? "Yenileniyor..." : "Yenile"}
                  </Button>
                </div>

                <div className="grid gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Ses dosyaları</div>
                  {visibleAudioLibrary.length ? (
                    visibleAudioLibrary.slice(0, 8).map((item) => (
                      <div key={item.path} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-800">
                        <div className="truncate text-xs text-slate-600 dark:text-slate-300">{item.name}</div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-full px-3 text-xs"
                          onClick={() => setDraft({ ...draft, audioUrl: item.url })}
                          disabled={formDisabled}
                        >
                          Seç
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500 dark:text-slate-400">Bu kayıt için henüz ses dosyası yok.</div>
                  )}
                </div>

                <div className="mt-4 grid gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Kapak görselleri</div>
                  {visibleCoverLibrary.length ? (
                    visibleCoverLibrary.slice(0, 8).map((item) => (
                      <div key={item.path} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-800">
                        <div className="truncate text-xs text-slate-600 dark:text-slate-300">{item.name}</div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-full px-3 text-xs"
                          onClick={() => setDraft({ ...draft, coverImageUrl: item.url })}
                          disabled={formDisabled}
                        >
                          Seç
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500 dark:text-slate-400">Bu kayıt için henüz kapak görseli yok.</div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Durum</div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Aktif</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Pasif ise kullanıcılar görmez.</div>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Premium</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Sadece premium üyeler görebilir.</div>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.isPremium}
                  onChange={(e) => setDraft({ ...draft, isPremium: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-amber-500 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div
              className={cn(
                "mt-4 inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                draft.active
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
              )}
            >
              {draft.active ? "Yayında" : "Taslak"}
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
              <Music2 className="h-4 w-4" />
              İpucu
            </div>

            <div className="mt-4 rounded-2xl border border-blue-600/15 bg-blue-600/5 p-4 text-sm text-slate-700 dark:border-blue-500/20 dark:text-slate-200">
              <div className="font-semibold">Etiketleme</div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Kişiye özel öneri için “Karmik Borç 1” gibi etiketler ekleyin. Karmik borcu olmayan kullanıcılar için “genel”
                etiketi kullanın.
              </div>
            </div>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
